import type { AiWidgetStrings } from './contracts'

export const defaultStrings: AiWidgetStrings = {
  defaultErrorMessage: 'Entschuldigung, es ist ein Fehler aufgetreten!',
  sendButtonLabel: 'Absenden',
  generics: { abort: 'Abbrechen', close: 'Schließen' },
  ai: {
    textTransform: {
      contextMenu: {
        title: 'Text transformieren',
        description: 'Transformiere den markierten Text mit KI',
        options: {
          spellcheck: 'Rechtschreibprüfung',
          shorten: 'Verkürze',
          lengthen: 'Verlängere',
          tone: 'Ton anpassen',
          toneProfessional: 'Professioneller',
          toneSimple: 'Einfacher',
          translate: 'Übersetze',
          translateEn: 'Englisch',
          translateDe: 'Deutsch',
        },
      },
      popover: {
        originalLabel: 'Original:',
        resultLabel: 'Ergebnis:',
        placeholderText: 'Verarbeitetes Ergebnis wird hier angezeigt...',
        variantLabel: (current: number, total: number) => `Variante ${current} / ${total}`,
        errorMessage: 'Fehler bei der Verarbeitung. Bitte versuchen Sie es erneut.',
        buttons: {
          close: 'Schließen',
          retry: 'Erneut versuchen',
          newSuggestion: 'Neu generieren',
          cancel: 'Abbrechen',
          apply: 'Übernehmen',
        },
        titles: {
          spellcheck: 'Rechtschreibprüfung',
          shorten: 'Text verkürzen',
          lengthen: 'Text verlängern',
          'tone-professional': 'Ton anpassen: Professioneller',
          'tone-simple': 'Ton anpassen: Einfacher',
          'translate-en': 'Übersetzen: Englisch',
          'translate-de': 'Übersetzen: Deutsch',
        },
      },
      button: {
        tooltip: 'Text transformieren',
      },
    },
    chat: {
      title: 'KI-Agent MAX',
      empty: {
        title: 'Hi, ich bin <b>MAX</b>.',
        description: 'Du kannst mir hier <b>offene Fragen zu orgaMAX Buchhaltung</b> stellen. Ich helfe dir direkt weiter.',
        faq: {
          title: 'FAQ öffnen:',
          link: 'FAQ-Bereich',
        },
        supportTicket: {
          title: 'Supportticket einreichen:',
          link: 'Ticket erstellen',
        },
        hint: 'Und kleiner Ausblick: <b>In Zukunft</b> kann ich noch deutlich mehr <b>Interaktionen gemeinsam mit dir und deinem Mandanten</b> übernehmen.',
      },
      input: {
        placeholder: 'Wie kann MAX dir helfen?',
        placeholderCtrlEnter: 'Wie kann MAX dir helfen? (Strg+Enter zum Senden)',
      },
      pill: {
        placeholder: 'Frag den KI-Agenten MAX …',
        send: 'Frage senden',
        dismiss: 'Eingabeleiste minimieren',
      },
      composer: {
        send: 'Nachricht senden',
        cancel: 'Anfrage abbrechen',
        cancelling: 'Abbruch wird angefordert',
        cancelled: 'Antwort wurde abgebrochen.',
        voiceStart: 'Spracheingabe starten',
        voiceStop: 'Spracheingabe beenden',
        voiceTranscribing: 'Sprache wird verarbeitet',
        context: {
          label: 'Seitenkontext',
          removeTooltip: 'Seitenkontext für diese Nachricht entfernen',
          enableTooltip: 'Seitenkontext aktivieren',
          enableLabel: 'Seitenkontext aktivieren',
          disabled: 'Seitenkontext deaktiviert',
        },
      },
      feedback: {
        tooltip: {
          noDetailedFeedback: 'Kein detailliertes Feedback abgegeben.',
        },
        tags: {
          title: 'Warum?',
          positive: [
            'Schnelle Antwort',
            'Präzise Information(en)',
            'Kreativität gezeigt',
            'Gut erklärt',
            'Anweisung perfekt befolgt',
          ],
          negative: [
            'Schlechter Schreibstil',
            'Falsche Informationen',
            'Nicht sachlich korrrekt',
            'Nicht relevant',
            'Zu lang',
            'Zu kurz',
            'Unvollständig',
          ],
        },
        comment: {
          title: 'Zusätzlicher Kommentar',
          placeholder: 'Schreibe hier dein Feedback.',
        },
        errors: {
          default: 'Beim Speichern des Feedbacks ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        },
      },
      button: 'Frag den KI-Agenten MAX',
      loading: {
        status: 'MAX denkt nach',
      },
      welcome: {
        default: {
          greeting: 'Hallo! 👋',
          greetingWithName: (name: string) => `Hallo ${name}! 👋`,
          introPrefix: 'Ich bin',
          introSuffix: 'dein KI-Agent.',
          support: 'Ich helfe dir gerne weiter.',
          question: 'Wobei kann ich dir helfen?',
        },
        trial: {
          greeting: 'Willkommen! 👋',
          greetingWithName: (name: string) => `Willkommen, ${name}! 👋`,
          introPrefix: 'Ich bin',
          introSuffix: 'dein KI-Agent und begleite dich durch deine Testphase.',
          support: 'Probier gerne die wichtigsten Funktionen aus — ich zeige dir, wie.',
          question: 'Womit möchtest du starten?',
        },
        customer: {
          greeting: 'Hallo! 👋',
          greetingWithName: (name: string) => `Hallo ${name}! 👋`,
          introPrefix: 'Ich bin',
          introSuffix: 'dein KI-Agent für orgaMAX.',
          support: 'Ich helfe dir gerne weiter.',
          question: 'Wobei kann ich dir helfen?',
        },
        admin: {
          greeting: 'Hallo! 👋',
          greetingWithName: (name: string) => `Hallo ${name}! 👋`,
          introPrefix: 'Ich bin',
          introSuffix: 'dein KI-Agent.',
          support: 'Ich unterstütze dich bei Verwaltung und Konfiguration.',
          question: 'Wobei kann ich dir helfen?',
        },
        churned: {
          greeting: 'Hallo! 👋',
          greetingWithName: (name: string) => `Hallo ${name}! 👋`,
          introPrefix: 'Ich bin',
          introSuffix: 'dein KI-Agent.',
          support: 'Ich sehe, dass dein Zugang abgelaufen oder gekündigt ist — ich helfe dir gerne, wenn du Fragen zur Weiternutzung hast.',
          question: 'Wobei kann ich dir helfen?',
        },
        context: {
          question: 'Womit kann ich dir helfen?',
          showGeneral: 'Allgemeine Hilfe',
          showContextPrefix: 'Hilfe für',
        },
      },
      header: {
        menu: 'Chat-Verlauf',
        newChat: 'Neuer Chat',
        settings: 'Einstellungen',
        back: 'Zurück zum Chat',
        close: 'Schließen',
        pin: 'Rechts andocken',
        unpin: 'Andockung lösen',
        dragHandle: 'Verschieben',
        minimize: 'Minimieren',
        expand: 'Maximieren',
        resize: 'Chatbreite ändern',
      },
      conversationList: {
        current: 'Aktueller Chat',
        previous: 'Frühere Chats',
        empty: 'Noch keine Chats vorhanden',
        newChat: 'Neuer Chat',
        startNew: 'Neuen Chat starten',
        loading: 'Lade weitere Chats...',
        expand: 'Chat-Verlauf einblenden',
        collapse: 'Chat-Verlauf ausblenden',
        title: 'Chat-Verlauf',
        delete: 'Chat löschen',
      },
      settings: {
        title: 'Einstellungen',
        sendMessageOption: {
          title: 'Nachricht senden mit',
          description: 'Wähle, wie du deine Nachricht versenden möchtest.',
          options: {
            enter: 'Enter',
            enterCtrl: 'Strg + Enter',
          },
        },
        sendAfterMicInput: {
          title: 'Nach Sprachaufnahme automatisch senden',
          description: 'Wähle, ob die Nachricht automatisch nach der Sprachaufnahme gesendet werden soll.',
          options: {
            yes: 'Ein',
            no: 'Aus',
          },
        },
        pillMode: {
          title: 'Eingabeleiste anzeigen',
          description: 'Zeigt unten eine kompakte Eingabeleiste statt der Schaltfläche. Beim Absenden öffnet sich der Assistent direkt mit deiner Frage.',
          minimized: {
            title: 'Eingabeleiste minimiert',
            description: 'Die Eingabeleiste ist für diese Sitzung zur Chat-Schaltfläche minimiert.',
            restore: 'Jetzt anzeigen',
          },
        },
        keepOpenOnClickAway: {
          title: 'Bei Klick außerhalb geöffnet lassen',
          description: 'Verhindert das automatische Schließen bei Klicks außerhalb. Du kannst den Chat weiterhin über die Schließen-Schaltflächen schließen.',
        },
        appearance: {
          title: 'Light-/ oder Darkmode',
          description: 'Wechsle zwischen heller und dunkler Darstellung.',
          dark: 'Darkmode',
          light: 'Lightmode',
        },
        cornerStyle: {
          title: 'Ecken des Widgets',
          description: 'Wähle die Form passend zum Einsatzbereich.',
          options: {
            rounded: 'Abgerundet',
            square: 'Kantig',
          },
        },
        quota: {
          title: 'KI-Kontingent',
          userTitle: 'Dein verbrauchtes Kontingent',
          tenantTitle: 'Mandanten-Kontingent',
          used: 'Verbraucht',
          available: 'Aktuell verfügbar',
          resetAt: 'Nächste Rücksetzung',
          tokens: 'Tokens',
          of: 'von',
          loading: 'Kontingent wird geladen …',
          error: 'Kontingent konnte nicht geladen werden.',
          retry: 'Erneut versuchen',
          userProgress: 'Verbrauch des persönlichen KI-Kontingents',
          tenantProgress: 'Verbrauch des Mandanten-Kontingents',
          blocked: {
            user: 'Dein persönliches KI-Kontingent ist ausgeschöpft.',
            tenant: 'Das Mandanten-Kontingent ist ausgeschöpft.',
            generic: 'Das KI-Kontingent ist ausgeschöpft.',
          },
        },
      },
      footer: {
        description: 'Der ChatBot MAX kann Fehler machen. Überprüfe die Antworten.',
      },
      toasts: {
        conversation: {
          deleted: 'Die Konversation wurde erfolgreich gelöscht.',
        },
      },
      errors: {
        default: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
        retry: 'Erneut versuchen',
        serviceUnavailable: 'Der KI-Dienst ist momentan nicht verfügbar. Bitte versuche es später erneut.',
        rateLimited: 'Es wurden zu viele KI-Anfragen gesendet. Bitte versuche es gleich noch einmal.',
        featureDisabled: 'Der KI-Chat ist für diesen Zugang nicht verfügbar.',
        conversationNotFound: 'Diese Unterhaltung wurde nicht gefunden. Bitte starte einen neuen Chat.',
        inputTooLarge: 'Deine Nachricht ist zu lang. Bitte kürze sie und versuche es erneut.',
        quota: {
          title: 'Dein KI-Kontingent ist aufgebraucht',
          resetAt: 'Das Kontingent wird zurückgesetzt am',
          contact: 'Kontakt aufnehmen',
          support: 'Support-Ticket erstellen',
        },
        message: {
          get: 'Die Nachricht konnte nicht abgerufen werden. Bitte versuche es erneut.',
          send: 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
          cancel: 'Die Anfrage konnte nicht abgebrochen werden. Bitte versuche es erneut.',
        },
        messages: {
          get: 'Die Nachrichten konnten nicht abgerufen werden. Bitte versuche es erneut.',
        },
        voice: {
          default: 'Beim Spracherkennen ist ein Fehler aufgetreten. Bitte versuche es erneut.',
          notSupported: 'Spracherkennung wird von deinem Browser nicht unterstützt. Bitte Chrome oder Edge verwenden.',
          notAllowed: 'Spracherkennung wird von deinem Browser nicht erlaubt. Bitte erlaube den Zugriff auf das Mikrofon in deinen Browser-Einstellungen.',
        },
        conversation: {
          delete: 'Die Konversation konnte nicht gelöscht werden. Bitte versuche es erneut.',
          get: 'Die Konversation konnte nicht abgerufen werden. Bitte versuche es erneut.',
        },
        conversations: {
          get: 'Die Konversationen konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
        },
      },
      answer: {
        actions: {
          info: 'Bewerte die Antwort oder kopiere den Text',
          thumbUp: 'Antwort hilfreich',
          thumbDown: 'Antwort nicht hilfreich',
          copy: 'Antwort kopieren',
          copied: 'Antwort kopiert',
          helpful: 'Antwort war hilfreich',
          notHelpful: 'Antwort war nicht hilfreich',
        },
        copyError: 'Die Antwort konnte nicht kopiert werden.',
      },
    },
  },
}
